// @ts-nocheck
import fieldSizeToButtonSize from "@store/Framework/Common/Utilities/fieldSizeToButtonSize";
import FlexWrapper, {
    flexWrapperGapStyles,
    FlexWrapperPresentationProps,
} from "@store/Library/Components/Toolkit/FlexWrapper";
import { FormFieldSizeVariant } from "@store/MobiusOverrides/FormField/FormField";
import ProductAddToCartButton, {
    ProductAddToCartButtonProps,
    useShowProductAddToCartButton,
} from "@store/Overrides/Components/ProductAddToCartButton";
import ProductQuantityOrdered, {
    ProductQuantityOrderedProps,
    useShowProductQuantityOrdered,
} from "@store/Overrides/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect, {
    ProductUnitOfMeasureSelectProps,
    useShowProductUnitOfMeasureSelect,
} from "@store/Overrides/Components/ProductUnitOfMeasureSelect";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { ButtonSizeVariant } from "@toolkit/Framework/Types/Button";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

type ExtendedProps<T> = T & {
    "data-test-selector"?: string;
};

export interface AddToCartOptionsAndButtonProps extends ExpandStyles<AddToCartOptionsAndButtonStyles> {
    sizeVariant?: FormFieldSizeVariant;
    buttonSizeVariant?: ButtonSizeVariant;
    showUnitOfMeasure?: boolean;
}

export interface AddToCartOptionsAndButtonStyles {
    flexWrapper?: FlexWrapperPresentationProps;
    unitOfMeasure?: ExtendedProps<ProductUnitOfMeasureSelectProps>;
    quantityOrdered?: ExtendedProps<ProductQuantityOrderedProps>;
    addToCartButton?: ExtendedProps<ProductAddToCartButtonProps>;
}

export const addToCartOptionsAndButtonStyles: AddToCartOptionsAndButtonStyles = {
    flexWrapper: {
        gap: pxToRem(10),
    },
    unitOfMeasure: {
        extendedStyles: {
            cssOverrides: {
                formField: css`
                    flex: 1 1 50%;
                    width: auto;
                `,
            },
        },
    },
    addToCartButton: {
        extendedStyles: {
            button: {
                css: css`
                    flex: 1 0 auto;
                    align-self: flex-end;
                `,
            },
        },
    },
};

const AddToCartOptionsAndButton: FC<AddToCartOptionsAndButtonProps> = ({
    extendedStyles,
    overridedStyles,
    sizeVariant = "small",
    buttonSizeVariant: baseButtonSizeVariant,
    showUnitOfMeasure = true,
}) => {
    const baseStyles = useExpandProps(addToCartOptionsAndButtonStyles, extendedStyles, overridedStyles);
    const styles = useMemo(() => {
        const buttonSizeVariant = baseButtonSizeVariant || fieldSizeToButtonSize(sizeVariant);
        const itemCss = flexWrapperGapStyles(baseStyles.flexWrapper?.gap, baseStyles.flexWrapper?.verticalGap);

        return mergeProps(baseStyles, {
            unitOfMeasure: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            quantityOrdered: {
                extendedStyles: {
                    sizeVariant,
                    cssOverrides: {
                        formField: itemCss,
                    },
                },
            },
            addToCartButton: {
                extendedStyles: {
                    button: {
                        sizeVariant: buttonSizeVariant,
                        css: itemCss,
                    },
                },
            },
        } as AddToCartOptionsAndButtonStyles);
    }, [baseStyles, sizeVariant]);

    const showProductAddToCartButton = useShowProductAddToCartButton(
        extendedStyles?.addToCartButton?.configurationCompleted,
        extendedStyles?.addToCartButton?.variantSelectionCompleted,
    );
    const showProductQuantityOrdered = useShowProductQuantityOrdered(
        extendedStyles?.quantityOrdered?.configurationCompleted,
        extendedStyles?.quantityOrdered?.variantSelectionCompleted,
    );
    const showProductUnitOfMeasureSelect = useShowProductUnitOfMeasureSelect();

    if (!showProductAddToCartButton && !showProductQuantityOrdered && !showProductUnitOfMeasureSelect) {
        return null;
    }

    return (
        <FlexWrapper wrap={false} {...styles.flexWrapper}>
            {showUnitOfMeasure && <ProductUnitOfMeasureSelect {...styles.unitOfMeasure} />}
            <ProductQuantityOrdered {...styles.quantityOrdered} />
            <ProductAddToCartButton {...styles.addToCartButton} />
        </FlexWrapper>
    );
};

export default AddToCartOptionsAndButton;
